import { Plus, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const aud = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(v || 0);
const fmtDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });

const EntryRow = ({ entry, onDelete, idx }) => (
  <div className="flex items-start gap-2 py-1.5 border-t border-gray-100 text-sm" data-testid={`task-time-entry-${idx}`}>
    <span className="w-24 shrink-0 text-gray-600">{fmtDate(entry.date)}</span>
    <span className="w-14 shrink-0 font-mono text-gray-900">{entry.hours}h</span>
    <span className="flex-1 text-gray-500 break-words">{entry.note || "—"}</span>
    <Button type="button" variant="ghost" size="icon" onClick={onDelete} className="h-7 w-7 text-gray-400 hover:text-red-600" data-testid={`task-time-delete-${idx}`}>
      <Trash2 className="w-3.5 h-3.5" />
    </Button>
  </div>
);

const NewEntry = ({ draft, setDraft, onAdd }) => (
  <div className="flex flex-wrap items-center gap-2 pt-2">
    <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} className="h-8 w-[140px] text-xs" data-testid="task-time-date-input" />
    <Input type="number" min="0" step="0.25" placeholder="Hrs" value={draft.hours} onChange={(e) => setDraft({ ...draft, hours: e.target.value })} className="h-8 w-[72px] text-xs" data-testid="task-time-hours-input" />
    <Input placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} className="h-8 flex-1 min-w-[120px] text-xs" data-testid="task-time-note-input" />
    <Button type="button" size="sm" variant="outline" onClick={onAdd} className="h-8 text-xs" data-testid="task-time-add-btn">
      <Plus className="w-3 h-3 mr-1" /> Add
    </Button>
  </div>
);

export const emptyTimeDraft = () => ({ date: today(), hours: "", note: "" });

// Quoted Amount + Time Log fields for the task dialog. All values live in the task form and save with it.
export const TaskFinancials = ({ form, setForm, defaultRate, draft, setDraft, onError }) => {
  const log = form.time_log || [];
  const totalHours = log.reduce((s, e) => s + (+e.hours || 0), 0);
  const rate = form.task_hourly_rate !== "" && form.task_hourly_rate != null ? +form.task_hourly_rate : +defaultRate || 0;

  const addEntry = () => {
    const hours = parseFloat(draft.hours);
    if (!draft.date || !(hours > 0) || hours > 24) return onError("Enter a date and hours between 0 and 24");
    const entry = { id: `te-${Date.now()}`, date: draft.date, hours, note: draft.note.trim() || null };
    setForm({ ...form, time_log: [...log, entry].sort((a, b) => a.date.localeCompare(b.date)) });
    setDraft({ ...emptyTimeDraft(), date: draft.date });
  };

  return (
    <div className="space-y-4 rounded-lg border border-gray-100 bg-gray-50/60 p-3" data-testid="task-financials">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="task-quoted">Quoted Amount (ex GST)</Label>
          <Input id="task-quoted" type="number" min="0" step="0.01" placeholder="Optional" value={form.quoted_amount}
            onChange={(e) => setForm({ ...form, quoted_amount: e.target.value })} data-testid="task-quoted-amount-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="task-rate">Hourly Rate (ex GST)</Label>
          <Input id="task-rate" type="number" min="0" step="0.01" placeholder={`Default $${defaultRate ?? "—"} (Labour Rate)`} value={form.task_hourly_rate}
            onChange={(e) => setForm({ ...form, task_hourly_rate: e.target.value })} data-testid="task-hourly-rate-input" />
        </div>
      </div>
      <div>
        <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
          <Clock className="w-4 h-4 text-indigo-600" /> Time Log
        </div>
        <div className="mt-1 max-h-40 overflow-y-auto" data-testid="task-time-log">
          {log.length === 0 && <p className="text-xs text-gray-400 py-1.5">No time logged yet.</p>}
          {log.map((e, i) => (
            <EntryRow key={e.id} entry={e} idx={i} onDelete={() => setForm({ ...form, time_log: log.filter((x) => x.id !== e.id) })} />
          ))}
        </div>
        <NewEntry draft={draft} setDraft={setDraft} onAdd={addEntry} />
        <div className="mt-2 flex justify-between text-xs text-gray-600" data-testid="task-time-total">
          <span>Total: <span className="font-mono font-semibold text-gray-900" data-testid="task-time-total-hours">{Math.round(totalHours * 100) / 100} hrs</span></span>
          <span>
            Value: <span className="font-mono font-semibold text-gray-900" data-testid="task-time-total-value">{aud(totalHours * rate)}</span> ex GST at {aud(rate)}/hr
          </span>
        </div>
      </div>
    </div>
  );
};
